<ng-container *ngIf="message">

  @if (editCompanion) {
    <!-- Edit-mode companion: the original-message card and the internal-notes card, no tabs.
         The Review & Approve editor renders this below the editor so staff can scroll to the
         customer's email and the notes while editing the reply (client ask 2026-09-13).
         Forward is hidden here because its dialog lives inside the Message tab. -->
    <ng-container *ngTemplateOutlet="inboundCard"></ng-container>
    <ng-container *ngTemplateOutlet="notesCard"></ng-container>
  } @else {
  <!-- ==================== UNIFIED TABBED LAYOUT ==================== -->
  <!-- Same tabbed structure for both pending and closed messages -->
  <div class="tabs-container">
    <!-- Tab Bar -->
    <div class="tab-bar">
      <button class="tab-button"
              [class.active]="activeDetailsTab === 'message'"
              (click)="setDetailsTab('message')">
        <i class="fa-solid fa-envelope"></i>
        <span>Message</span>
      </button>
      <button class="tab-button"
              [class.active]="activeDetailsTab === 'ai-analysis'"
              (click)="setDetailsTab('ai-analysis')">
        <i class="fa-solid fa-brain"></i>
        <span>AI Analysis</span>
      </button>
      <button class="tab-button"
              [class.active]="activeDetailsTab === 'timeline'"
              (click)="setDetailsTab('timeline')">
        <i class="fa-solid fa-clock-rotate-left"></i>
        <span>Timeline</span>
      </button>
      <button class="tab-button" *ngIf="isSystemDeveloper && message.AIAgentRunID"
              [class.active]="activeDetailsTab === 'agent-run'"
              (click)="setDetailsTab('agent-run')">
        <i class="fa-solid fa-terminal"></i>
        <span>Agent Run</span>
      </button>
      <button class="tab-button" *ngIf="isSystemDeveloper"
              [class.active]="activeDetailsTab === 'developer'"
              (click)="setDetailsTab('developer')">
        <i class="fa-solid fa-code"></i>
        <span>Developer</span>
      </button>
    </div>

    <!-- Tab Content -->
    <div class="tab-content" #tabContent>

      <!-- ========== MESSAGE TAB ========== -->
      <div class="tab-pane" *ngIf="activeDetailsTab === 'message'">

        <!-- Skip Info (shown when message was skipped or manually marked not required).
             Escalated messages defer to the escalation summary bar above the
             viewer — it already carries the category + full reasoning. -->
        <div class="skip-alert" *ngIf="(message.GenerationStatus === 'Skipped' || message.ApprovalStatus === 'Not Required') && message.SkipCategory && message.ApprovalStatus !== 'Escalated'">
          <div class="skip-icon">
            <i class="fa-solid" [ngClass]="getSkipIconClass(message.SkipCategory)"></i>
          </div>
          <div class="skip-details">
            <span class="skip-category-badge" [ngClass]="getSkipCategoryClass(message.SkipCategory)">
              {{ formatSkipCategory(message.SkipCategory) }}
            </span>
            <span class="skip-label">Message Skipped</span>
            <span class="spam-badge" *ngIf="message.IsSpam">
              <i class="fa-solid fa-ban"></i> Spam
            </span>
          </div>
        </div>

        <!-- Error Alert (shown when message has error) -->
        <ng-container *ngIf="getFriendlyError() as friendlyError">
          <div class="error-alert error-alert-v2">
            <div class="error-icon">
              <i class="fa-solid fa-circle-exclamation"></i>
            </div>
            <div class="error-body">
              <div class="error-header">
                <span class="error-type-badge" [ngClass]="friendlyError.cssClass">{{ friendlyError.title }}</span>
              </div>
              <div class="error-summary">{{ friendlyError.summary }}</div>
              <div class="error-action" *ngIf="friendlyError.action">{{ friendlyError.action }}</div>
              <div class="error-controls">
                <button type="button" class="error-link-btn" (click)="toggleErrorDetail()" *ngIf="message.ErrorMessage">
                  <i class="fa-solid" [ngClass]="isErrorDetailExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                  {{ isErrorDetailExpanded ? 'Hide details' : 'Show details' }}
                </button>
                <button type="button" class="error-cta-btn" (click)="openAICredentials()" *ngIf="friendlyError.isAICredentialIssue">
                  <i class="fa-solid fa-key"></i> Check AI credentials
                </button>
              </div>
              <pre class="error-raw" *ngIf="isErrorDetailExpanded && message.ErrorMessage">{{ message.ErrorMessage }}</pre>
            </div>
          </div>
        </ng-container>

        <!-- Forward record: a staff-initiated forward (no inbound half, no AI reply).
             Rendered as its own event so it can't masquerade as a member message or an
             Izzy-generated response. -->
        <div class="email-section forward-record" *ngIf="isForwardRecord">
          <div class="email-divider">
            <span class="divider-line"></span>
            <span class="divider-label"><i class="fa-solid fa-share"></i> Forwarded</span>
            <span class="divider-line"></span>
          </div>
          <div class="email-header">
            <div class="email-avatar">
              <i class="fa-solid fa-share"></i>
            </div>
            <div class="email-meta">
              <div class="email-sender">Forwarded by staff</div>
              <div class="email-recipients" *ngIf="message.SentToRecipients">To: {{ message.SentToRecipients }}</div>
              <div class="email-recipients" *ngIf="message.SentCCRecipients">Cc: {{ message.SentCCRecipients }}</div>
              <div class="email-time" *ngIf="message.SentAt">{{ formatDate(message.SentAt) }}</div>
            </div>
            <button class="forward-button" type="button" [disabled]="isExportingPdf"
                    title="Export this message as a PDF"
                    (click)="exportPdf()">
              <i class="fa-solid" [ngClass]="isExportingPdf ? 'fa-spinner fa-spin' : 'fa-file-pdf'"></i> Export
            </button>
          </div>
          <div class="forward-error" *ngIf="exportPdfError">
            <i class="fa-solid fa-triangle-exclamation"></i> {{ exportPdfError }}
          </div>
          <div class="email-subject" *ngIf="message.Subject">
            <strong>{{ message.Subject }}</strong>
          </div>
          <div class="forward-note-label">Note included with the forward:</div>
          <div class="email-body markdown-content" [innerHTML]="renderMarkdown(message.SentContent)"></div>
        </div>

        <!-- Staff-originated message (Manual Messages, #289): compose or follow-up, born
             Pending. No inbound half, no AI draft — its own framing, like forwards. -->
        @if (isStaffRecord) {
          <div class="email-section staff-record">
            <div class="staff-record-banner">
              <span class="staff-record-icon">
                <i class="fa-solid" [class.fa-pen-to-square]="message.OutboundType === 'New'" [class.fa-reply]="message.OutboundType === 'Reply'"></i>
              </span>
              <div class="staff-record-meta">
                <div class="staff-record-title">{{ staffRecordLabel }}</div>
                <div class="staff-record-sub">
                  @if (message.SentAt) {
                    Sent to {{ message.SentToRecipients || message.Recipient }} · {{ formatRelativeTime(message.SentAt) }}
                  } @else {
                    To {{ message.Recipient }} · created {{ formatRelativeTime(message.__mj_CreatedAt) }} · not sent yet
                  }
                </div>
              </div>
              @if (message.SentAt) {
                <button class="forward-button staff-record-export" type="button" [disabled]="isExportingPdf"
                        title="Export this message as a PDF"
                        (click)="exportPdf()">
                  <i class="fa-solid" [ngClass]="isExportingPdf ? 'fa-spinner fa-spin' : 'fa-file-pdf'"></i> Export
                </button>
              }
            </div>
            @if (exportPdfError) {
              <div class="forward-error">
                <i class="fa-solid fa-triangle-exclamation"></i> {{ exportPdfError }}
              </div>
            }
            @if (message.Subject) {
              <div class="email-subject">
                <strong>{{ message.Subject }}</strong>
              </div>
            }
            <div class="email-body markdown-content" [innerHTML]="renderMarkdown(staffRecordContent)"></div>
            <!-- Outbound attachments on this staff message (renders nothing when there are none).
                 Sent only: while Pending, the recipient bar's editable Files row owns display. -->
            @if (message.ID && message.SentAt) {
              <app-message-attachments
                [messageId]="message.ID"
                [direction]="'Outbound'"
                (open)="onAttachmentOpen($event)">
              </app-message-attachments>
            }
          </div>
        }

        <!-- Forward dialog: staff forward of this inbound email via the server-side action.
             Suppression + recipient cap are enforced server-side; refusals surface here. -->
        <div class="forward-overlay" *ngIf="showForwardDialog" (click)="closeForwardDialog()">
          <div class="forward-dialog" (click)="$event.stopPropagation()">
            <div class="forward-dialog-header">
              <i class="fa-solid fa-share"></i>
              <span>Forward message</span>
              <button type="button" class="forward-close" (click)="closeForwardDialog()" [disabled]="isForwarding">
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>
            <ng-container *ngIf="!forwardSuccess; else forwardDone">
              <div class="forward-field">
                <label>To:</label>
                <input type="text" [(ngModel)]="forwardTo" [disabled]="isForwarding"
                       placeholder="Addresses, separated by commas" />
              </div>
              <div class="forward-field">
                <label>Cc:</label>
                <input type="text" [(ngModel)]="forwardCc" [disabled]="isForwarding"
                       placeholder="Optional" />
              </div>
              <div class="forward-field">
                <label>Note:</label>
                <textarea [(ngModel)]="forwardComment" rows="3" [disabled]="isForwarding"
                          placeholder="Optional note included with the forwarded message"></textarea>
              </div>
              <div class="forward-error" *ngIf="forwardError">
                <i class="fa-solid fa-triangle-exclamation"></i> {{ forwardError }}
              </div>
              <div class="forward-actions">
                <button type="button" class="forward-cancel" (click)="closeForwardDialog()" [disabled]="isForwarding">Cancel</button>
                <button type="button" class="forward-send" (click)="submitForward()"
                        [disabled]="!forwardTo.trim() || isForwarding">
                  <i class="fa-solid fa-spinner fa-spin" *ngIf="isForwarding"></i>
                  {{ isForwarding ? 'Forwarding…' : 'Forward' }}
                </button>
              </div>
            </ng-container>
            <ng-template #forwardDone>
              <div class="forward-success">
                <i class="fa-solid fa-circle-check"></i>
                Forwarded. The forward is recorded on the timeline.
              </div>
              <div class="forward-actions">
                <button type="button" class="forward-send" (click)="showForwardDialog = false">Close</button>
              </div>
            </ng-template>
          </div>
        </div>

        <!-- ========== HERO: Izzy draft card ========== -->
        <!-- The AI draft is the primary object on this screen. One card, one
             state chip (draftState) — the old status banners are gone. -->
        <div class="draft-card" *ngIf="!isForwardRecord && !isStaffRecord && (isPending || isClosed)">
          <div class="draft-card-header">
            <span class="draft-izzy-chip"><img src="/assets/izzy-mark.svg" alt="Izzy"></span>
            <span class="draft-title">Izzy draft</span>
            <span class="edited-indicator" *ngIf="wasUserEdited">
              <i class="fa-solid fa-pencil"></i> edited
            </span>
            <span class="draft-state-chip" [ngClass]="draftState.cls">
              <i class="fa-solid" [ngClass]="draftState.icon"></i> {{ draftState.label }}
            </span>
            <span class="draft-time" *ngIf="draftTimestamp" [title]="formatDate(draftTimestamp)">{{ formatRelativeTime(draftTimestamp) }}</span>
            <button class="forward-button translate-button draft-translate" type="button" *ngIf="canTranslateDraft"
                    [class.active]="draftTranslation.status === 'shown'"
                    [disabled]="draftTranslation.status === 'loading'"
                    [title]="draftTranslation.status === 'shown' ? 'Show the draft as written' : 'Translate this draft for reading (the draft itself is not changed)'"
                    (click)="toggleTranslate('draft')">
              <i class="fa-solid" [ngClass]="draftTranslation.status === 'loading' ? 'fa-spinner fa-spin' : 'fa-language'"></i>
              {{ draftTranslation.status === 'shown' ? 'Original' : 'Translate' }}
            </button>
            <!-- View Mode Toggle — pending (only when content was actually edited) -->
            <div class="reply-view-toggle" *ngIf="isPending && message.GeneratedReplyContent && message.ApprovedReplyContent && message.ApprovedReplyContent !== message.GeneratedReplyContent">
              <button class="toggle-btn" [class.active]="replyViewMode === 'approved'" (click)="setReplyViewMode('approved')">Current</button>
              <button class="toggle-btn" [class.active]="replyViewMode === 'generated'" (click)="setReplyViewMode('generated')">Generated</button>
              <button class="toggle-btn" [class.active]="replyViewMode === 'diff'" (click)="setReplyViewMode('diff')">Diff</button>
            </div>
            <!-- View Mode Toggle — closed -->
            <div class="reply-view-toggle" *ngIf="isClosed && displayReplyContent && message.GeneratedReplyContent">
              <button class="toggle-btn" [class.active]="closedReplyViewMode === 'sent'" (click)="setClosedReplyViewMode('sent')">Sent</button>
              <!-- Only show Approved and Diff if user made actual edits -->
              <button class="toggle-btn" *ngIf="wasUserEdited" [class.active]="closedReplyViewMode === 'approved'" (click)="setClosedReplyViewMode('approved')">Approved</button>
              <button class="toggle-btn" [class.active]="closedReplyViewMode === 'generated'" (click)="setClosedReplyViewMode('generated')">Generated</button>
              <button class="toggle-btn" *ngIf="wasUserEdited" [class.active]="closedReplyViewMode === 'diff'" (click)="setClosedReplyViewMode('diff')">Diff</button>
            </div>
          </div>

          <!-- Translate view for the draft: picker, then the translation in place of the body.
               Read-only — approving/editing always works on the draft as written. -->
          @if (draftTranslation.status === 'picking' || draftTranslation.status === 'loading') {
            <div class="translate-bar in-draft">
              <i class="fa-solid fa-language"></i>
              <label class="translate-label">Translate to</label>
              <select class="translate-select" [(ngModel)]="translateLanguage" [disabled]="draftTranslation.status === 'loading'">
                @for (lang of translationLanguages; track lang) {
                  <option [value]="lang">{{ lang }}</option>
                }
              </select>
              <button type="button" class="translate-run" (click)="runTranslate('draft')" [disabled]="draftTranslation.status === 'loading'">
                @if (draftTranslation.status === 'loading') {
                  <i class="fa-solid fa-spinner fa-spin"></i> Translating…
                } @else {
                  Translate
                }
              </button>
              <button type="button" class="translate-cancel" (click)="cancelTranslate('draft')" [disabled]="draftTranslation.status === 'loading'">Cancel</button>
            </div>
          }
          @if (draftTranslation.status === 'error') {
            <div class="forward-error translate-error in-draft">
              <i class="fa-solid fa-triangle-exclamation"></i> {{ draftTranslation.error }}
              <button type="button" class="translate-link" (click)="toggleTranslate('draft')">Dismiss</button>
            </div>
          }
          @if (draftTranslation.status === 'shown') {
            <div class="translated-banner in-draft">
              <i class="fa-solid fa-language"></i>
              <span>Translated from {{ draftTranslation.detectedLanguage }} to {{ draftTranslation.language }} for reading only. The reply goes out as written.</span>
              <button type="button" class="translate-link" (click)="toggleTranslate('draft')">Show original</button>
            </div>
            <div class="email-body reply-body markdown-content translated" [innerHTML]="renderMarkdown(draftTranslation.translatedText)"></div>
          }

          <!-- Draft body — PENDING -->
          <ng-container *ngIf="isPending && draftTranslation.status !== 'shown'">
            <ng-container *ngIf="message.GeneratedReplyContent">
              <!-- Show content based on view mode (only when edited) -->
              <ng-container *ngIf="message.ApprovedReplyContent && message.ApprovedReplyContent !== message.GeneratedReplyContent">
                <div class="email-body reply-body markdown-content" *ngIf="replyViewMode === 'approved'" [innerHTML]="renderMarkdown(message.ApprovedReplyContent)"></div>
                <div class="email-body reply-body markdown-content generated" *ngIf="replyViewMode === 'generated'" [innerHTML]="renderMarkdown(message.GeneratedReplyContent)"></div>
                <div class="diff-view-wrapper" *ngIf="replyViewMode === 'diff'">
                  <izzy-diff-viewer
                    [originalContent]="message.GeneratedReplyContent || ''"
                    [modifiedContent]="message.ApprovedReplyContent || ''"
                    [showLegend]="true"
                    title="Changes">
                  </izzy-diff-viewer>
                </div>
              </ng-container>
              <!-- Show approved/current content directly when same as generated -->
              <div class="email-body reply-body markdown-content" *ngIf="message.ApprovedReplyContent && message.ApprovedReplyContent === message.GeneratedReplyContent" [innerHTML]="renderMarkdown(message.ApprovedReplyContent)"></div>
              <!-- Show generated content directly when no edits -->
              <div class="email-body reply-body markdown-content" *ngIf="!message.ApprovedReplyContent" [innerHTML]="renderMarkdown(message.GeneratedReplyContent)"></div>
            </ng-container>
            <!-- No Reply Generated Yet -->
            <div class="draft-placeholder" *ngIf="!message.GeneratedReplyContent && message.GenerationStatus !== 'Skipped'">
              <i class="fa-solid fa-robot"></i>
              <span>No AI reply generated yet</span>
            </div>
          </ng-container>

          <!-- Draft body — CLOSED -->
          <ng-container *ngIf="isClosed && draftTranslation.status !== 'shown'">
            <ng-container *ngIf="displayReplyContent">
              <!-- Show content based on view mode -->
              <ng-container *ngIf="message.GeneratedReplyContent">
                <div class="email-body reply-body markdown-content" *ngIf="closedReplyViewMode === 'sent'" [innerHTML]="renderMarkdown(message.SentContent || message.ApprovedReplyContent)"></div>
                <div class="email-body reply-body markdown-content" *ngIf="closedReplyViewMode === 'approved' && wasUserEdited" [innerHTML]="renderMarkdown(message.ApprovedReplyContent)"></div>
                <div class="email-body reply-body markdown-content generated" *ngIf="closedReplyViewMode === 'generated'" [innerHTML]="renderMarkdown(message.GeneratedReplyContent)"></div>
                <div class="diff-view-wrapper" *ngIf="closedReplyViewMode === 'diff' && wasUserEdited">
                  <izzy-diff-viewer
                    [originalContent]="message.GeneratedReplyContent || ''"
                    [modifiedContent]="message.ApprovedReplyContent || ''"
                    [showLegend]="true"
                    title="Changes">
                  </izzy-diff-viewer>
                </div>
              </ng-container>
              <!-- Show sent content directly when no generated content -->
              <div class="email-body reply-body markdown-content" *ngIf="!message.GeneratedReplyContent" [innerHTML]="renderMarkdown(message.SentContent || displayReplyContent)"></div>
            </ng-container>
            <!-- No Reply State -->
            <div class="draft-placeholder" *ngIf="!displayReplyContent && message.GenerationStatus !== 'Skipped' && message.ApprovalStatus !== 'Escalated'">
              <i class="fa-solid fa-comment-slash"></i>
              <span>No reply generated</span>
            </div>
            <!-- Escalated with no draft -->
            <div class="draft-placeholder" *ngIf="!displayReplyContent && message.ApprovalStatus === 'Escalated'">
              <i class="fa-solid fa-triangle-exclamation"></i>
              <span>No draft — escalated for human review</span>
            </div>
            <!-- Skip/escalation reasoning lives here with the draft. For
                 Escalated it always renders when reasoning exists (the top
                 summary bar stays compact); this is the single copy. -->
            <div class="skip-notice-box" *ngIf="((message.GenerationStatus === 'Skipped' && !displayReplyContent) || (message.ApprovalStatus === 'Not Required' && message.SkipCategory) || message.ApprovalStatus === 'Escalated') && message.SkipReasoning">
              <div class="skip-icon-wrapper">
                <i class="fa-solid" [ngClass]="getSkipIconClass(message.SkipCategory)"></i>
              </div>
              <div class="skip-info">
                <span class="skip-category-badge" [ngClass]="getSkipCategoryClass(message.SkipCategory)">
                  {{ formatSkipCategory(message.SkipCategory) }}
                </span>
                <!-- Whole reason, rendered as markdown; collapsed to ~4 lines with Show more /
                     Show less rather than a hard character cut (the old 200-char cut dropped
                     the part staff needed). -->
                @if (message.SkipReasoning) {
                  <div class="skip-reason-text markdown"
                       [class.collapsed]="!isSkipReasoningExpanded && skipReasonNeedsToggle(message.SkipReasoning)"
                       [innerHTML]="renderMarkdown(message.SkipReasoning)"></div>
                  @if (skipReasonNeedsToggle(message.SkipReasoning)) {
                    <button type="button" class="skip-reason-toggle" (click)="toggleSkipReasoning()">
                      {{ isSkipReasoningExpanded ? 'Show less' : 'Show more' }}
                    </button>
                  }
                }
              </div>
            </div>
          </ng-container>

          <!-- Outbound attachments that traveled with the sent reply — footer band
               inside the card (renders nothing when there are none). Sent only:
               while Pending, the recipient bar's editable Files row owns display. -->
          @if (isClosed && message.SentAt && message.ID) {
            <app-message-attachments
              [messageId]="message.ID"
              [direction]="'Outbound'"
              label="Sent with this reply"
              variant="cardFooter"
              (open)="onAttachmentOpen($event)">
            </app-message-attachments>
          }
        </div>

        <!-- ========== Original message — quiet, below the draft ==========
             Markup lives in the #inboundCard template at the bottom of this file so
             edit-companion mode can render the same card outside the tabs. -->
        <ng-container *ngTemplateOutlet="inboundCard"></ng-container>

        <!-- ========== Internal notes — staff-only, on THIS message (2026-09-13) ==========
             Markup lives in the #notesCard template at the bottom of this file so edit-companion
             mode can render the same card below the original message. -->
        <ng-container *ngTemplateOutlet="notesCard"></ng-container>

        <!-- Quality Feedback Bar (shown for any message with AI-generated content) -->
        <div class="feedback-bar" *ngIf="showFeedbackBar">
          <span class="feedback-label">Was this response helpful?</span>
          <div class="feedback-buttons">
            <button class="feedback-btn thumbs-up"
                    [class.active]="message.QualityFeedback === 'Up'"
                    [disabled]="feedbackSaving"
                    (click)="submitQualityFeedback('Up')"
                    title="Helpful response">
              <i class="fa-solid fa-thumbs-up"></i>
            </button>
            <button class="feedback-btn thumbs-down"
                    [class.active]="message.QualityFeedback === 'Down'"
                    [disabled]="feedbackSaving"
                    (click)="submitQualityFeedback('Down')"
                    title="Needs improvement">
              <i class="fa-solid fa-thumbs-down"></i>
            </button>
          </div>
          <span class="feedback-saved-indicator" *ngIf="feedbackSaved">
            <i class="fa-solid fa-check"></i> Saved
          </span>
          <i class="fa-solid fa-spinner fa-spin feedback-spinner" *ngIf="feedbackSaving"></i>
        </div>
      </div>

      <!-- ========== AI ANALYSIS TAB ========== -->
      <div class="tab-pane" *ngIf="activeDetailsTab === 'ai-analysis'">
        <!-- Extracted to <izzy-ai-analysis>: the Ticket screen renders the identical pane. -->
        <izzy-ai-analysis
          [message]="message"
          [isRescoring]="isRescoring"
          [canRescore]="canRescore"
          [isReprocessing]="isReprocessing"
          [organizationId]="organizationId"
          (rescoreRequested)="rescoreRequested.emit()">
        </izzy-ai-analysis>
      </div>

      <!-- ========== TIMELINE TAB ========== -->
      <div class="tab-pane" *ngIf="activeDetailsTab === 'timeline'">
        <div class="timeline-list">
          <!-- Received -->
          <div class="timeline-item" *ngIf="message.ReceivedAt">
            <div class="timeline-marker received">
              <i class="fa-solid fa-envelope"></i>
            </div>
            <div class="timeline-content">
              <div class="timeline-title">Received</div>
              <div class="timeline-time">{{ formatDate(message.ReceivedAt) }}</div>
              <div class="timeline-meta" *ngIf="message.Sender">from {{ message.Sender }}</div>
            </div>
          </div>

          <!-- AI Generated -->
          <div class="timeline-item" *ngIf="message.GenerationStatus === 'Generated' && message.GeneratedReplyContent">
            <div class="timeline-marker generated">
              <i class="fa-solid fa-robot"></i>
            </div>
            <div class="timeline-content">
              <div class="timeline-title">AI Generated</div>
              <div class="timeline-time" *ngIf="message.AIEvaluatedAt">{{ formatDate(message.AIEvaluatedAt) }}</div>
              <div class="timeline-meta" *ngIf="message.AIConfidenceScore !== null">Confidence: {{ message.AIConfidenceScore }}%</div>
            </div>
          </div>

          <!-- AI Skipped -->
          <div class="timeline-item" *ngIf="message.GenerationStatus === 'Skipped'">
            <div class="timeline-marker skipped">
              <i class="fa-solid fa-forward"></i>
            </div>
            <div class="timeline-content">
              <div class="timeline-title">AI Skipped</div>
              <div class="timeline-time" *ngIf="message.AIEvaluatedAt">{{ formatDate(message.AIEvaluatedAt) }}</div>
              <div class="timeline-meta" *ngIf="message.SkipCategory">{{ formatSkipCategory(message.SkipCategory) }}</div>
            </div>
          </div>

          <!-- AI Failed / Send Failed -->
          <div class="timeline-item"
               [class.send-error]="message.ErrorType && !message.SentAt && isSendError(message.ErrorType)"
               *ngIf="message.GenerationStatus === 'Failed'">
            <div class="timeline-marker failed">
              <i class="fa-solid fa-circle-exclamation"></i>
            </div>
            <div class="timeline-content">
              <div class="timeline-title">{{ (message.ErrorType && !message.SentAt && isSendError(message.ErrorType)) ? 'Send Failed' : 'AI Failed' }}</div>
              <div class="timeline-time" *ngIf="message.AIEvaluatedAt">{{ formatDate(message.AIEvaluatedAt) }}</div>
              <div class="timeline-meta" *ngIf="message.ErrorType">{{ formatErrorType(message.ErrorType) }}</div>
            </div>
          </div>

          <!-- Edited -->
          <div class="timeline-item" *ngIf="message.ApprovedReplyContent && message.ApprovedReplyContent !== message.GeneratedReplyContent">
            <div class="timeline-marker edited">
              <i class="fa-solid fa-pencil"></i>
            </div>
            <div class="timeline-content">
              <div class="timeline-title">Edited</div>
              <div class="timeline-meta">Content modified before approval</div>
            </div>
          </div>

          <!-- Auto-Approved -->
          <div class="timeline-item" *ngIf="message.ApprovalStatus === 'Approved' && message.AIAutoApproved">
            <div class="timeline-marker auto-approved">
              <i class="fa-solid fa-bolt"></i>
            </div>
            <div class="timeline-content">
              <div class="timeline-title">Auto-Approved</div>
              <div class="timeline-time" *ngIf="message.ApprovedAt">{{ formatDate(message.ApprovedAt) }}</div>
              <div class="timeline-meta">Confidence exceeded threshold</div>
            </div>
          </div>

          <!-- Manually Approved -->
          <div class="timeline-item" *ngIf="message.ApprovalStatus === 'Approved' && !message.AIAutoApproved">
            <div class="timeline-marker approved">
              <i class="fa-solid fa-check"></i>
            </div>
            <div class="timeline-content">
              <div class="timeline-title">Manually Approved</div>
              <div class="timeline-time" *ngIf="message.ApprovedAt">{{ formatDate(message.ApprovedAt) }}</div>
              <div class="timeline-meta" *ngIf="message.ApprovalFeedback">"{{ message.ApprovalFeedback }}"</div>
            </div>
          </div>

          <!-- Rejected -->
          <div class="timeline-item" *ngIf="message.ApprovalStatus === 'Rejected'">
            <div class="timeline-marker rejected">
              <i class="fa-solid fa-times"></i>
            </div>
            <div class="timeline-content">
              <div class="timeline-title">Rejected</div>
              <div class="timeline-time" *ngIf="message.ApprovedAt">{{ formatDate(message.ApprovedAt) }}</div>
              <div class="timeline-meta" *ngIf="message.ApprovalFeedback">"{{ message.ApprovalFeedback }}"</div>
            </div>
          </div>

          <!-- Sent -->
          <div class="timeline-item" *ngIf="message.SentContent">
            <div class="timeline-marker sent">
              <i class="fa-solid fa-paper-plane"></i>
            </div>
            <div class="timeline-content">
              <div class="timeline-title">Sent</div>
              <div class="timeline-time" *ngIf="message.SentAt">{{ formatDate(message.SentAt) }}</div>
              <div class="timeline-meta" *ngIf="message.Recipient">to {{ message.Recipient }}</div>
            </div>
          </div>

          <!-- Pending indicator for pending messages -->
          <div class="timeline-item" *ngIf="isPending">
            <div class="timeline-marker pending">
              <i class="fa-solid fa-hourglass-half"></i>
            </div>
            <div class="timeline-content">
              <div class="timeline-title">Awaiting Review</div>
              <div class="timeline-meta">Message is pending approval</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ========== AGENT RUN TAB ========== -->
      <div class="tab-pane" *ngIf="activeDetailsTab === 'agent-run' && isSystemDeveloper">
        <!-- Loading State -->
        <div class="agent-run-loading" *ngIf="agentRunLoading">
          <i class="fa-solid fa-spinner fa-spin"></i>
          <span>Loading agent run data...</span>
        </div>

        <!-- Error State -->
        <div class="agent-run-error" *ngIf="agentRunError">
          <i class="fa-solid fa-circle-exclamation"></i>
          <span>{{ agentRunError }}</span>
        </div>

        <!-- Agent Run Payload -->
        <div class="agent-run-content" *ngIf="agentRunPayload && !agentRunLoading && !agentRunError">
          <izzy-payload-viewer [payload]="agentRunPayload"></izzy-payload-viewer>
        </div>

        <!-- No Agent Run -->
        <div class="empty-tab-state" *ngIf="!message.AIAgentRunID">
          <i class="fa-solid fa-terminal"></i>
          <span>No agent run associated with this message</span>
        </div>
      </div>

      <!-- ========== DEVELOPER TAB ========== -->
      <div class="tab-pane" *ngIf="activeDetailsTab === 'developer' && isSystemDeveloper">
        <div class="developer-grid">
          <div class="dev-row">
            <label>Message ID:</label>
            <code class="dev-code">{{ message.ID }}</code>
          </div>
          <div class="dev-row" *ngIf="message.ChannelID">
            <label>Channel ID:</label>
            <code class="dev-code">{{ message.ChannelID }}</code>
          </div>
          <div class="dev-row" *ngIf="message.AIAgentRunID">
            <label>Agent Run:</label>
            <button class="dev-link-btn" (click)="openAgentRun(message.AIAgentRunID)">
              <code>{{ message.AIAgentRunID }}</code>
              <i class="fa-solid fa-external-link-alt"></i>
            </button>
          </div>
          <div class="dev-row" *ngIf="!message.AIAgentRunID">
            <label>Agent Run:</label>
            <span class="dev-muted">Not available</span>
          </div>
          <div class="dev-row">
            <label>Generation:</label>
            <span class="status-badge" [ngClass]="getGenerationStatusClass(message.GenerationStatus)">{{ message.GenerationStatus }}</span>
          </div>
          <div class="dev-row">
            <label>Approval:</label>
            <span class="approval-badge" [ngClass]="getApprovalStatusClass(message.ApprovalStatus)">{{ message.ApprovalStatus }}</span>
          </div>
          <div class="dev-row" *ngIf="message.ExternalSystemRecordID">
            <label>External ID:</label>
            <code class="dev-code">{{ message.ExternalSystemRecordID }}</code>
          </div>
          <div class="dev-row" *ngIf="message.ThreadID">
            <label>Thread ID:</label>
            <code class="dev-code">{{ message.ThreadID }}</code>
          </div>
        </div>
      </div>

    </div>
  </div>
  }

  <!-- Original message card. Rendered inside the Message tab in the normal layout and
       on its own in edit-companion mode; one copy of the markup for both. -->
  <ng-template #inboundCard>
    <div class="email-section inbound" *ngIf="!isForwardRecord && !isStaffRecord" [class.collapsed]="isInboundCollapsed">
      <div class="inbound-microlabel">Original message</div>
      <div class="email-header clickable" (click)="toggleInboundCollapse()">
        <div class="email-avatar">
          <i class="fa-solid fa-user"></i>
        </div>
        <div class="email-meta">
          <div class="email-sender">{{ message.Sender || 'Unknown' }}</div>
          <div class="email-recipients" *ngIf="message.ToRecipients">To: {{ message.ToRecipients }}</div>
          <div class="email-recipients" *ngIf="message.CCRecipients">Cc: {{ message.CCRecipients }}</div>
          <div class="email-time" *ngIf="message.ReceivedAt">{{ formatDate(message.ReceivedAt) }}</div>
        </div>
        <button class="forward-button" type="button" *ngIf="canForward && !editCompanion"
                title="Forward this email to other recipients"
                (click)="$event.stopPropagation(); openForwardDialog()">
          <i class="fa-solid fa-share"></i> Forward
        </button>
        <button class="forward-button translate-button" type="button" *ngIf="canTranslateInbound"
                [class.active]="inboundTranslation.status === 'shown'"
                [disabled]="inboundTranslation.status === 'loading'"
                [title]="inboundTranslation.status === 'shown' ? 'Show the original message' : 'Translate this message for reading (never sent to the customer)'"
                (click)="$event.stopPropagation(); toggleTranslate('inbound')">
          <i class="fa-solid" [ngClass]="inboundTranslation.status === 'loading' ? 'fa-spinner fa-spin' : 'fa-language'"></i>
          {{ inboundTranslation.status === 'shown' ? 'Original' : 'Translate' }}
        </button>
        <button class="forward-button" type="button" [disabled]="isExportingPdf"
                title="Export this message as a PDF"
                (click)="$event.stopPropagation(); openExportDialog()">
          <i class="fa-solid" [ngClass]="isExportingPdf ? 'fa-spinner fa-spin' : 'fa-file-pdf'"></i> Export
        </button>
        <button class="collapse-toggle" type="button" [attr.aria-expanded]="!isInboundCollapsed" (click)="$event.stopPropagation(); toggleInboundCollapse()">
          <i class="fa-solid" [ngClass]="isInboundCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
        </button>
      </div>
      <div class="forward-error" *ngIf="exportPdfError && !showExportDialog">
        <i class="fa-solid fa-triangle-exclamation"></i> {{ exportPdfError }}
      </div>

      <!-- Export dialog: mirrors the ticket screen's export panel — internal notes are opt-in
           because exported files often leave Izzy. Lives in the card so it works in both the
           Message tab and edit-companion mode. -->
      @if (showExportDialog) {
        <div class="forward-overlay" (click)="closeExportDialog()">
          <div class="forward-dialog" (click)="$event.stopPropagation()">
            <div class="forward-dialog-header">
              <i class="fa-solid fa-file-pdf"></i>
              <span>Export message as PDF</span>
              <button type="button" class="forward-close" (click)="closeExportDialog()" [disabled]="isExportingPdf">
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>
            <p class="export-hint">
              Downloads this message and its reply as a PDF. Internal notes are left out unless
              you include them — exported files often leave Izzy.
            </p>
            <label class="export-notes-choice">
              <input type="checkbox" [(ngModel)]="exportIncludeInternalNotes" [disabled]="isExportingPdf || messageNotes.length === 0" />
              @if (messageNotes.length === 0) {
                Include internal notes (none on this message)
              } @else {
                Include internal notes ({{ messageNotes.length }}) — labeled INTERNAL NOTE in the PDF
              }
            </label>
            @if (exportPdfError) {
              <div class="forward-error"><i class="fa-solid fa-triangle-exclamation"></i> {{ exportPdfError }}</div>
            }
            <div class="forward-actions">
              <button type="button" class="forward-cancel" (click)="closeExportDialog()" [disabled]="isExportingPdf">Cancel</button>
              <button type="button" class="forward-send" (click)="exportPdf()" [disabled]="isExportingPdf">
                @if (isExportingPdf) {
                  <i class="fa-solid fa-spinner fa-spin"></i>
                }
                {{ isExportingPdf ? 'Exporting…' : 'Export PDF' }}
              </button>
            </div>
          </div>
        </div>
      }
      <!-- Translate view: language picker, then a banner over the translated text. Read-only —
           the original stays the record; this only changes what the reviewer sees. -->
      @if (!isInboundCollapsed && (inboundTranslation.status === 'picking' || inboundTranslation.status === 'loading')) {
        <div class="translate-bar">
          <i class="fa-solid fa-language"></i>
          <label class="translate-label">Translate to</label>
          <select class="translate-select" [(ngModel)]="translateLanguage" [disabled]="inboundTranslation.status === 'loading'">
            @for (lang of translationLanguages; track lang) {
              <option [value]="lang">{{ lang }}</option>
            }
          </select>
          <button type="button" class="translate-run" (click)="runTranslate('inbound')" [disabled]="inboundTranslation.status === 'loading'">
            @if (inboundTranslation.status === 'loading') {
              <i class="fa-solid fa-spinner fa-spin"></i> Translating…
            } @else {
              Translate
            }
          </button>
          <button type="button" class="translate-cancel" (click)="cancelTranslate('inbound')" [disabled]="inboundTranslation.status === 'loading'">Cancel</button>
        </div>
      }
      @if (!isInboundCollapsed && inboundTranslation.status === 'error') {
        <div class="forward-error translate-error">
          <i class="fa-solid fa-triangle-exclamation"></i> {{ inboundTranslation.error }}
          <button type="button" class="translate-link" (click)="toggleTranslate('inbound')">Dismiss</button>
        </div>
      }
      @if (!isInboundCollapsed && inboundTranslation.status === 'shown') {
        <div class="translated-banner">
          <i class="fa-solid fa-language"></i>
          <span>Translated from {{ inboundTranslation.detectedLanguage }} to {{ inboundTranslation.language }} for reading only. The original is what the sender wrote.</span>
          <button type="button" class="translate-link" (click)="toggleTranslate('inbound')">Show original</button>
        </div>
      }
      <div class="email-subject" *ngIf="inboundDisplaySubject && !isInboundCollapsed">
        <strong>{{ inboundDisplaySubject }}</strong>
      </div>
      <div class="email-body markdown-content" *ngIf="!isInboundCollapsed" [class.translated]="inboundTranslation.status === 'shown'" [innerHTML]="renderMarkdown(inboundDisplayContent)"></div>

      <!-- Non-inline INBOUND attachments (renders nothing when message has none).
           Direction-scoped: staff-attached outbound files must never render here
           as if the sender had attached them. -->
      <app-message-attachments
        *ngIf="!isInboundCollapsed && message?.ID"
        [messageId]="message.ID"
        [direction]="'Inbound'"
        (open)="onAttachmentOpen($event)">
      </app-message-attachments>
    </div>
  </ng-template>

  <!-- Internal notes card — message-level counterpart of ticket internal notes. Append-only;
       never sent to the customer; also surfaces in the linked ticket's timeline (one way).
       Rendered inside the Message tab normally and below the original message in
       edit-companion mode; one copy of the markup for both. -->
  <ng-template #notesCard>
    <div class="notes-section">
      <div class="notes-header">
        <i class="fa-solid fa-note-sticky"></i>
        <span class="notes-title">Internal notes</span>
        @if (messageNotes.length > 0) {
          <span class="notes-count">{{ messageNotes.length }}</span>
        }
        <span class="notes-hint">Staff only. Never sent to the customer.</span>
      </div>
      @if (notesLoading) {
        <div class="notes-status"><i class="fa-solid fa-spinner fa-spin"></i> Loading notes…</div>
      }
      @if (notesError) {
        <div class="notes-error"><i class="fa-solid fa-triangle-exclamation"></i> {{ notesError }}</div>
      }
      @for (n of messageNotes; track n.ID) {
        <div class="note-item">
          <div class="note-meta">
            <span class="note-author">{{ n.AuthorPerson || n.AuthorEmail || 'Staff' }}</span>
            <span class="note-badge">Internal</span>
            <span class="note-when">{{ formatDate(n.__mj_CreatedAt) }}</span>
          </div>
          <div class="note-body">{{ n.Body }}</div>
        </div>
      } @empty {
        @if (!notesLoading && !notesError) {
          <div class="notes-status notes-empty">No notes yet.</div>
        }
      }
      <div class="note-composer">
        <textarea class="note-input"
                  [(ngModel)]="noteDraft"
                  rows="2"
                  placeholder="Add an internal note (staff-only, never sent to the customer)…"
                  [disabled]="isAddingNote"
                  (keydown.control.enter)="addNote()"
                  (keydown.meta.enter)="addNote()"></textarea>
        <div class="note-actions">
          <span class="note-shortcut">Ctrl/⌘ + Enter to add</span>
          <button type="button" class="note-add-btn" (click)="addNote()" [disabled]="!noteDraft.trim() || isAddingNote">
            @if (isAddingNote) {
              <i class="fa-solid fa-spinner fa-spin"></i>
            } @else {
              <i class="fa-solid fa-plus"></i>
            }
            Add note
          </button>
        </div>
      </div>
    </div>
  </ng-template>

</ng-container>

<!-- No Message Selected -->
<div class="empty-state" *ngIf="!message">
  <i class="fa-solid fa-envelope-open-text"></i>
  <p>Select a message to view details</p>
</div>

<!-- Attachment preview drawer (slides in from the right when a chip is clicked) -->
<app-attachment-drawer
  #attachmentDrawer
  [currentUser]="currentUser"
  [environmentId]="environmentId">
</app-attachment-drawer>
